/**
 * Hand-supplied vessel art.
 *
 * When an image is present it replaces the entire procedural vessel - glass,
 * cables, collar and brain - and the live layers are composited on top of it:
 * a heat tint over the tissue, a clock-driven glow, rising bubbles, cracks at
 * the redline, and the meltdown drain.
 *
 * Everything is positioned in fractions of the drawn image rectangle, so the
 * overlays track the art at any size.
 *
 * See docs/vessel-art.md for how to swap the artwork.
 */

const CANDIDATES = [
  '/brainfryer_image.jpg',
  '/vessel.png',
  '/vessel.jpg',
  '/vessel.webp',
  '/brain.png',
  '/brain.webp',
  '/brain.jpg',
];

/**
 * Where things sit inside the artwork, as fractions of its width and height.
 * Measured off brainfryer_image.jpg (848 x 482).
 */
export const ART_LAYOUT = {
  /** The tissue itself, for tinting, glow and cracks. */
  brain: { cx: 0.505, cy: 0.52, rx: 0.15, ry: 0.21 },
  /** The fluid column, for bubbles. */
  fluid: { x0: 0.35, x1: 0.665, y0: 0.19, y1: 0.9 },
  /** The three readout labels baked into the art, which we paint over. */
  labels: { x: 0.694, ys: [0.432, 0.502, 0.575], size: 0.036 },
} as const;

export interface VesselArt {
  image: HTMLImageElement | null;
  ready: boolean;
  /** Natural aspect ratio, for sizing the canvas to the art. */
  aspect: number;
}

export function loadVesselArt(onReady?: (art: VesselArt) => void): VesselArt {
  const art: VesselArt = { image: null, ready: false, aspect: 1.76 };
  if (typeof Image === 'undefined') return art;

  let index = 0;
  const attempt = (): void => {
    const src = CANDIDATES[index];
    if (src === undefined) return;
    index += 1;

    const img = new Image();
    img.onload = () => {
      art.image = img;
      art.ready = true;
      if (img.height > 0) art.aspect = img.width / img.height;
      onReady?.(art);
    };
    // Try the next name; when the list runs out the procedural vessel stays.
    img.onerror = attempt;
    img.src = src;
  };
  attempt();

  return art;
}
